import { useMemo } from "react";
import ReactECharts from "echarts-for-react";

import { useAdminFeedbackStats } from "../../api/feedback";
import { baseAxis, baseTooltip, echarts, PALETTE } from "../../charts/echarts";

const KIND_COLORS: Record<string, string> = {
  bug: "#fb7185",
  feature: "#a78bfa",
  other: "#94a3b8",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "#94a3b8",
  triaged: "#38bdf8",
  planned: "#fbbf24",
  in_progress: "#f59e0b",
  deployed: "#34d399",
  wont_do: "#475569",
  duplicate: "#475569",
  open: "#38bdf8",
  shipped: "#34d399",
  rejected: "#475569",
};

function fmtDuration(secs: number | null): string {
  if (secs == null) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  const m = secs / 60;
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

export default function Stats() {
  const stats = useAdminFeedbackStats(90);

  const byStatusOption = useMemo<echarts.EChartsCoreOption | null>(() => {
    const rows = stats.data?.by_status ?? [];
    if (!rows.length) return null;
    return {
      tooltip: { ...baseTooltip, axisPointer: { type: "shadow" } },
      grid: { left: 80, right: 12, top: 12, bottom: 24 },
      xAxis: { ...baseAxis, type: "value", minInterval: 1 },
      yAxis: { ...baseAxis, type: "category", data: rows.map((r) => r.status) },
      series: [
        {
          type: "bar",
          data: rows.map((r) => ({
            value: r.count,
            itemStyle: { color: STATUS_COLORS[r.status] ?? "#94a3b8" },
          })),
        },
      ],
    };
  }, [stats.data]);

  const byKindDayOption = useMemo<echarts.EChartsCoreOption | null>(() => {
    const rows = stats.data?.by_kind_day ?? [];
    if (!rows.length) return null;
    const days = Array.from(new Set(rows.map((r) => r.day))).sort();
    const kinds = Array.from(new Set(rows.map((r) => r.kind))).sort();
    const series = kinds.map((kind) => {
      const map = new Map<string, number>();
      for (const r of rows) if (r.kind === kind) map.set(r.day, r.count);
      return {
        name: kind,
        type: "line" as const,
        smooth: true,
        showSymbol: false,
        itemStyle: { color: KIND_COLORS[kind] ?? "#94a3b8" },
        areaStyle: { opacity: 0.12 },
        data: days.map((d) => map.get(d) ?? 0),
      };
    });
    return {
      tooltip: { ...baseTooltip },
      legend: { data: kinds, top: 0, textStyle: { color: PALETTE.muted, fontSize: 10 } },
      grid: { left: 36, right: 12, top: 30, bottom: 24 },
      xAxis: { ...baseAxis, type: "category", data: days },
      yAxis: { ...baseAxis, type: "value", minInterval: 1 },
      series,
    };
  }, [stats.data]);

  const clusterByStatusOption = useMemo<echarts.EChartsCoreOption | null>(() => {
    const rows = stats.data?.cluster_by_status ?? [];
    if (!rows.length) return null;
    return {
      tooltip: { ...baseTooltip, axisPointer: { type: "shadow" } },
      grid: { left: 80, right: 12, top: 12, bottom: 24 },
      xAxis: { ...baseAxis, type: "value", minInterval: 1 },
      yAxis: { ...baseAxis, type: "category", data: rows.map((r) => r.status) },
      series: [
        {
          type: "bar",
          data: rows.map((r) => ({
            value: r.count,
            itemStyle: { color: STATUS_COLORS[r.status] ?? "#94a3b8" },
          })),
        },
      ],
    };
  }, [stats.data]);

  if (stats.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  }
  if (!stats.data) {
    return <div className="p-6 text-sm text-slate-500">No data.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label="median time-to-triage"
          value={fmtDuration(stats.data.median_time_to_triage_seconds)}
          sub="pending → first non-pending"
        />
        <Stat
          label="open items"
          value={String(
            (stats.data.by_status.find((r) => r.status === "pending")?.count ?? 0) +
              (stats.data.by_status.find((r) => r.status === "triaged")?.count ?? 0),
          )}
          sub="pending + triaged"
        />
        <Stat
          label="open clusters"
          value={String(
            stats.data.cluster_by_status.find((r) => r.status === "open")?.count ?? 0,
          )}
        />
      </div>

      <Card title="Open by status · last 90d">
        {byStatusOption ? (
          <ReactECharts
            option={byStatusOption}
            style={{ height: 220, width: "100%" }}
            notMerge
            lazyUpdate
            echarts={echarts}
          />
        ) : (
          <div className="py-8 text-center text-sm text-slate-500">No items.</div>
        )}
      </Card>

      <Card title="Bugs vs features · last 90d">
        {byKindDayOption ? (
          <ReactECharts
            option={byKindDayOption}
            style={{ height: 220, width: "100%" }}
            notMerge
            lazyUpdate
            echarts={echarts}
          />
        ) : (
          <div className="py-8 text-center text-sm text-slate-500">No items.</div>
        )}
      </Card>

      <Card title="Clusters by status">
        {clusterByStatusOption ? (
          <ReactECharts
            option={clusterByStatusOption}
            style={{ height: 200, width: "100%" }}
            notMerge
            lazyUpdate
            echarts={echarts}
          />
        ) : (
          <div className="py-8 text-center text-sm text-slate-500">No clusters yet.</div>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
        {title}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-xl tabular-nums text-slate-100">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}
